import { useId } from "react";
import type { LeftoverChoice } from "@shared/staff-settlement";
import type { Settlement } from "./use-staff-step10";
import "./staff-settlement-box.css";
import { useStaffAdvances, advancePaid } from "./use-staff-advances";

export type StaffSettlementBoxProps = {
  mode: "absence" | "leaving";
  firstName: string;
  rtl: boolean;
  settlement: Settlement;
  choices: LeftoverChoice[];
  onChoicesChange: (v: LeftoverChoice[]) => void;
  returnedIds: string[];
  onReturnChange?: (id: string, value: boolean) => void;
  recorderName?: string;
  recorderId?: string;
};

type ChoiceKind = "employee" | "branch_manager" | "branch_loss" | "recorder" | "other";

const money = (value: number | string | null | undefined) =>
  value == null || !Number.isFinite(Number(value))
    ? "—"
    : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const amount = (value: number | string | null | undefined) =>
  value == null || !Number.isFinite(Number(value)) ? 0 : Number(value);

export default function StaffSettlementBox({
  mode,
  firstName,
  rtl,
  settlement,
  choices,
  onChoicesChange,
  returnedIds,
  onReturnChange,
  recorderName,
  recorderId,
}: StaffSettlementBoxProps) {
  const uid = useId();
  const advanceEvidence = useStaffAdvances();
  const text = (en: string, ar: string) => (rtl ? ar : en);
  const returned = new Set(returnedIds);
  const basis = settlement.calculationBasis;
  const advanceTaken = settlement.advances.reduce((sum, item) => sum + amount(item.takenFromPay), 0);
  const deductionTaken =
     settlement.deductions.filter((item) => !("itemType" in item)).reduce((sum, item) => sum + amount(item.takenFromPay), 0) +
    settlement.uniforms.reduce((sum, item) => sum + amount(item.takenFromPay), 0);
  const paidToEmployee = amount(settlement.payable);
  const notCovered = amount(settlement.uncovered);
  const chartTotal = paidToEmployee + advanceTaken + deductionTaken + notCovered;
  const pct = (value: number) => (chartTotal > 0 ? `${(value / chartTotal) * 100}%` : "0%");
  const leftoverItems = settlement.leftovers;
  const manualItems = leftoverItems.filter((item) => item.itemType !== "advance");
   const openAdvances = settlement.advances.filter((item) => amount(item.outstanding) > 0);
  const totalDebt =
    settlement.advances.reduce((sum, item) => sum + amount(item.outstanding), 0) +
     settlement.deductions.filter((item) => !("itemType" in item)).reduce((sum, item) => sum + amount(item.amount), 0) +
    settlement.uniforms.reduce((sum, item) => sum + amount(item.amount), 0);

  const findChoice = (itemType: LeftoverChoice["itemType"], itemId: string) =>
    choices.find((choice) => choice.itemType === itemType && choice.itemId === itemId);

  const updateChoice = (
    itemType: LeftoverChoice["itemType"],
    itemId: string,
    update: Partial<LeftoverChoice>,
  ) => {
    const existing = findChoice(itemType, itemId);
    const next: LeftoverChoice = existing
      ? { ...existing, ...update }
      : { itemType, itemId, responsibilityType: "employee", userIds: [], reason: "", ...update };
    onChoicesChange([
      ...choices.filter((choice) => !(choice.itemType === itemType && choice.itemId === itemId)),
      next,
    ]);
  };

  const clearChoice = (itemType: LeftoverChoice["itemType"], itemId: string) =>
    onChoicesChange(choices.filter((choice) => !(choice.itemType === itemType && choice.itemId === itemId)));

  const setResponsibility = (itemType: LeftoverChoice["itemType"], itemId: string, kind: ChoiceKind) => {
    const userIds =
      kind === "employee"
        ? settlement.employeeUserId
          ? [settlement.employeeUserId]
          : []
        : kind === "recorder"
          ? recorderId
            ? [recorderId]
            : []
          : [];
    const responsibilityType: LeftoverChoice["responsibilityType"] =
      kind === "employee"
        ? "employee"
        : kind === "branch_manager"
          ? "branch_manager"
          : kind === "branch_loss"
            ? "branch_loss"
            : kind === "recorder"
              ? "recorder"
              : "other";
    updateChoice(itemType, itemId, { responsibilityType, userIds });
  };

  const setPerson = (itemType: LeftoverChoice["itemType"], itemId: string, id: string) => {
    const choice = findChoice(itemType, itemId);
    if (choice) updateChoice(itemType, itemId, { userIds: id ? [id] : [] });
  };

  const personOptions = (kind: "branch_manager" | "other") =>
    settlement.responsibilityUsers.filter((person) => kind === "other" || person.branchManager);

  const fullName = (id: string) => settlement.responsibilityUsers.find((person) => person.id === id)?.name ?? id;
   const dateRange = settlement.days === 0 ? text("No unpaid working days", "لا توجد أيام عمل غير مدفوعة") : `${settlement.payFrom || basis.employee.hireDate} → ${settlement.payTo || basis.lastDay}`;

  return (
    <section className={`staff-settlement-box ${rtl ? "ssb-rtl" : ""}`} dir={rtl ? "rtl" : "ltr"} aria-label={text("Staff settlement", "تسوية الموظف")}>
      <header className="ssb-heading">
        <div>
          <p className="ssb-eyebrow">{text(mode === "absence" ? "HELD PAY" : "FINAL ACCOUNT", mode === "absence" ? "الراتب المحجوز" : "الحساب النهائي")}</p>
          <h3>
            {mode === "absence"
              ? text(`Still open with ${firstName}`, `ما يزال مفتوحًا مع ${firstName}`)
              : text(`Settlement with ${firstName}`, `التسوية مع ${firstName}`)}
          </h3>
        </div>
        <span className="ssb-currency">EGP</span>
      </header>

      {mode === "absence" ? (
        <div className="ssb-absence">
          <div
            className={`ssb-absence-bar ${settlement.payKnown && notCovered === 0 ? "is-covered" : "is-open"}`}
            role="img"
            aria-label={
              settlement.payKnown
                ? text(`Held pay coverage: ${money(Math.max(totalDebt - notCovered, 0))} of ${money(totalDebt)} EGP`, `تغطية الراتب المحجوز: ${money(Math.max(totalDebt - notCovered, 0))} من ${money(totalDebt)} جنيه`)
                : text("Held pay coverage is not yet known", "تغطية الراتب المحجوز غير معروفة بعد")
            }
          >
            <span
              style={{
                width: settlement.payKnown
                  ? totalDebt > 0
                    ? `${(Math.max(totalDebt - notCovered, 0) / totalDebt) * 100}%`
                    : "100%"
                  : "100%",
              }}
            />
          </div>
          <div className="ssb-grid" role="table" aria-label={text("Held pay details", "تفاصيل الراتب المحجوز")}>
            <div className="ssb-grid-row" role="row">
              <div className="ssb-grid-label" role="rowheader">{text("Advances", "السُلف")}</div>
              <div className="ssb-grid-value" role="cell">
                {settlement.advances.length ? (
                  <div className="ssb-advance-list">
                    {settlement.advances.map((advance) => (
                      <div className="ssb-advance-chip" key={advance.id}>
                         <strong>{advance.number} · {money(advance.outstanding)} EGP</strong>
                        <span>{advance.givenByName}</span>
                      </div>
                    ))}
                  </div>
                ) : <span className="ssb-muted">{text("None", "لا يوجد")}</span>}
              </div>
            </div>
            <div className="ssb-grid-row" role="row">
              <div className="ssb-grid-label" role="rowheader">{text("Held pay", "الراتب المحجوز")}</div>
              <div className="ssb-grid-value" role="cell">
                <strong>{money(settlement.payDue)} EGP</strong>
                <small>{dateRange}{settlement.days != null ? ` · ${settlement.days} ${text("days", "يومًا")}` : ""}</small>
              </div>
            </div>
            <div className="ssb-grid-row" role="row">
              <div className="ssb-grid-label" role="rowheader">{text("Uniform pieces", "قطع الزي")}</div>
              <div className="ssb-grid-value" role="cell">
                {settlement.uniforms.length
                  ? settlement.uniforms.map((uniform) => (
                      <span className="ssb-piece" key={uniform.id}>{uniform.quantity} × {uniform.name}</span>
                    ))
                  : <span className="ssb-muted">0</span>}
              </div>
            </div>
            <div className={`ssb-grid-row ssb-total ${settlement.payKnown ? (notCovered === 0 ? "is-covered" : "is-open") : "is-unknown"}`} role="row">
              <div className="ssb-grid-label" role="rowheader">{text("Total", "الإجمالي")}</div>
              <div className="ssb-grid-value" role="cell">
                {!settlement.payKnown
                  ? text("Not yet known", "غير معروف بعد")
                  : notCovered === 0
                    ? <>{text("Covered by held pay", "مغطى من الراتب المحجوز")} <span aria-hidden="true">✓</span></>
                    : <>{text("Not covered", "غير مغطى")} · {money(notCovered)} EGP</>}
              </div>
            </div>
          </div>
          <p className="ssb-note">
            {text(
               `On saving, ${openAdvances.length} ${openAdvances.length === 1 ? "advance remains" : "advances remain"} unpaid. Finance and the givers will be alerted. ${settlement.payKnown && notCovered === 0 ? "Held pay covers it; this is not a repayment." : "Any uncovered amount will be decided at end of service."}`,
               `عند الحفظ، تظل ${openAdvances.length} سلفة غير مدفوعة، وسيُنبّه قسم المالية ومقدّمو السُلف. ${settlement.payKnown && notCovered === 0 ? "يغطيها الراتب المحجوز، ولا يُعد ذلك سدادًا." : "يُحسم أي مبلغ غير مغطى عند إنهاء الخدمة."}`,
            )}
          </p>
        </div>
      ) : (
        <>
          {!settlement.payKnown ? (
            <div className="ssb-unknown" role="status">
              <strong>{text("Pay amount unavailable", "مبلغ الراتب غير متاح")}</strong>
              <span>{text("The debt total is shown for review. No responsibility choices can be made until pay is known.", "يظهر إجمالي المديونية للمراجعة. لا يمكن اختيار المسؤولية حتى يُعرف الراتب.")}</span>
              <b>{money(totalDebt)} EGP</b>
            </div>
          ) : (
            <>
              <div className="ssb-bar" role="img" aria-label={text("Settlement allocation", "توزيع التسوية")}>
                <span className="ssb-segment ssb-paid" style={{ width: pct(paidToEmployee) }} />
                <span className="ssb-segment ssb-advances" style={{ width: pct(advanceTaken) }} />
                <span className="ssb-segment ssb-deductions" style={{ width: pct(deductionTaken) }} />
                <span className="ssb-segment ssb-uncovered" style={{ width: pct(notCovered) }} />
              </div>
              <div className="ssb-legend" aria-label={text("Settlement legend", "مفتاح التسوية")}>
                <span><i className="ssb-paid" />{text("Paid to him", "مدفوع له")}</span>
                <span><i className="ssb-advances" />{text("Advances taken", "السُلف المخصومة")}</span>
                <span><i className="ssb-deductions" />{text("Deductions taken · uniforms included", "الخصومات · تشمل الزي")}</span>
                <span><i className="ssb-uncovered" />{text("Not covered", "غير مغطى")}</span>
              </div>
            </>
          )}

          <div className="ssb-money-rows">
            <div className="ssb-money-row ssb-pay-row">
              <span>{text("Pay due", "الراتب المستحق")}<small>{dateRange}{settlement.days != null ? ` · ${settlement.days} ${text("days", "يومًا")}` : ""}</small></span>
              <strong>{money(settlement.payDue)} <small>EGP</small></strong>
            </div>
            {settlement.advances.map((advance) => {
              const partial = amount(advance.takenFromPay) < amount(advance.owed);
              return (
                <details className="ssb-money-row ssb-expandable" key={advance.id}>
                  <summary>
                    <span>{text("Advance taken", "السلفة المخصومة")} · {advance.number}<small>{partial ? `${money(advance.takenFromPay)} ${text("of full owed", "من كامل المبلغ المستحق")} ${money(advance.owed)} EGP` : text("Taken from pay", "مخصومة من الراتب")}</small></span>
                    <strong>{money(advance.takenFromPay)} <small>EGP</small></strong>
                  </summary>
                  <div className="ssb-detail-grid">
                    <span>{text("Given date", "تاريخ الصرف")}<b>{advance.givenOn || "—"}</b></span>
                    <span>{text("Giver", "مقدّم السلفة")}<b>{advance.givenByName || "—"}</b></span>
                     <span>{text("Original amount", "المبلغ الأصلي")}<b>{money(advanceEvidence.query.data?.advances.find(a => a.id === advance.id)?.principalAmount)} EGP</b></span>
                     <span>{text("Paid back", "المسدّد")}<b>{(() => { const a = advanceEvidence.query.data?.advances.find(a => a.id === advance.id); return a ? `${money(advancePaid(a))} EGP` : "—"; })()}</b></span>
                    <span>{text("Still owed", "المتبقي")}<b>{money(advance.owed)} EGP</b></span>
                  </div>
                </details>
              );
            })}
             {settlement.deductions.filter((item) => !("itemType" in item)).map((deduction) => (
              <div className="ssb-money-row" key={deduction.id}>
                 <span>{text("Waiting deduction", "خصم معلّق")} · {deduction.number}<small>{deduction.typeName} · {text("Due", "المستحق")}: {money(deduction.amount)} EGP · {text("Left", "المتبقي")}: {money(deduction.left)} EGP</small></span>
                <strong>{money(deduction.takenFromPay)} <small>EGP</small></strong>
              </div>
            ))}
            {settlement.uniforms.map((uniform) => (
              <div className="ssb-money-row ssb-uniform-row" key={uniform.id}>
                <span>
                  {text("Uniform", "الزي")} · {uniform.name}
                  <small>{uniform.quantity} {text("pieces", "قطع")} · {money(uniform.unitDeduction)} {text("each", "للقطعة")}</small>
                </span>
                <strong>{money(uniform.takenFromPay)} <small>EGP</small></strong>
                <label className="ssb-return">
                  <input
                    type="checkbox"
                    checked={returned.has(uniform.id)}
                    aria-label={text(`Mark ${uniform.name} as returned`, `تحديد ${uniform.name} على أنه مُعاد`)}
                    onChange={(event) => onReturnChange?.(uniform.id, event.currentTarget.checked)}
                    disabled={!onReturnChange}
                  />
                  {text("Returned", "تم الإرجاع")}
                </label>
              </div>
            ))}
             {!settlement.uniforms.length && <div className="ssb-money-row"><span>{text("Uniform pieces held", "قطع الزي بحوزته")}</span><strong>0</strong></div>}
            <div className="ssb-money-row ssb-final">
              <span>{text("Final pay", "صافي الراتب")}</span>
              <strong>{money(settlement.payable)} <small>EGP</small></strong>
            </div>
          </div>

          {settlement.payKnown && leftoverItems.length > 0 && (
            <section className="ssb-leftovers" aria-labelledby={`${uid}-leftovers`}>
              <h4 id={`${uid}-leftovers`}>{text("Not covered", "مبالغ غير مغطاة")} <span>{money(notCovered)} EGP</span></h4>
              <div className="ssb-leftover-list">
                {leftoverItems.map((item) => {
                  if (item.itemType === "advance") {
                    const advance = settlement.advances.find((entry) => entry.id === item.itemId);
                    const joint = item.responsibility?.type === "giver_and_recorder";
                    return (
                      <div className="ssb-leftover ssb-auto" key={`${item.itemType}-${item.itemId}`}>
                        <div className="ssb-leftover-head">
                          <strong>{advance?.number || text("Advance", "سلفة")}</strong>
                          <b>{money(item.amount)} EGP</b>
                        </div>
                        <span className="ssb-rule">{text("Advance rule", "القاعدة")}</span>
                        <p>
                          {joint ? text("Both responsible", "مسؤولان معًا") : text("Giver second in line", "مقدّم السلفة مسؤول ثانٍ")}
                          {advance?.givenByName ? ` · ${advance.givenByName}` : ""}
                          {joint ? ` · ${recorderName || text("recorder", "مسجل العملية")}` : ""}
                        </p>
                      </div>
                    );
                  }

                  const existing = findChoice(item.itemType, item.itemId);
                  const selection = existing?.responsibilityType;
                  const secondPersonType = selection === "branch_manager" ? "branch_manager" : selection === "other" ? "other" : null;
                  const candidates = secondPersonType ? personOptions(secondPersonType) : [];
                  const secondPersonId = existing?.userIds[0] || "";
                  const choiceKey = `${uid}-${item.itemType}-${item.itemId}`;
                  return (
                    <div className="ssb-leftover ssb-manual" key={`${item.itemType}-${item.itemId}`}>
                      <div className="ssb-leftover-head">
                         <strong>{item.itemType === "uniform" ? text("Uniform", "الزي") : text("Deduction", "خصم")} · {settlement.uniforms.find(u => u.id === item.itemId)?.name || settlement.deductions.find(d => d.id === item.itemId)?.number || item.itemId}</strong>
                        <b>{money(item.amount)} EGP</b>
                      </div>
                      <span className="ssb-rule">{text("Your call", "قرارك")}</span>
                      <label className="ssb-field" htmlFor={`${choiceKey}-who`}>{text("Who covers it?", "من يتحمل المبلغ؟")}</label>
                      <select
                        id={`${choiceKey}-who`}
                        value={
                          selection === "employee" ? "employee" :
                          selection === "branch_manager" ? "branch_manager" :
                          selection === "branch_loss" ? "branch_loss" :
                          selection === "recorder" ? "recorder" :
                          selection === "other" ? "other" : ""
                        }
                        onChange={(event) => {
                          const value = event.currentTarget.value;
                          if (!value) clearChoice(item.itemType, item.itemId);
                          else setResponsibility(item.itemType, item.itemId, value as ChoiceKind);
                        }}
                        required
                      >
                        <option value="">{text("Choose responsibility", "اختر المسؤولية")}</option>
                         <option value="employee">{text("Employee", "الموظف")}</option>
                        {personOptions("branch_manager").length > 0 && <option value="branch_manager">{text("Branch manager", "مدير الفرع")}</option>}
                        <option value="branch_loss">{text("Branch loss", "خسارة على الفرع")}</option>
                        {recorderId && <option value="recorder">{text("Me", "أنا")}{recorderName ? ` · ${recorderName}` : ""}</option>}
                        <option value="other">{text("Someone else", "شخص آخر")}</option>
                      </select>
                      {secondPersonType && (
                        <label className="ssb-field">
                          {secondPersonType === "branch_manager" ? text("Choose branch manager", "اختر مدير الفرع") : text("Choose person", "اختر الشخص")}
                          <select
                            value={secondPersonId}
                            onChange={(event) => setPerson(item.itemType, item.itemId, event.currentTarget.value)}
                            required
                          >
                            <option value="">{text("Select a person", "اختر شخصًا")}</option>
                            {candidates.map((person) => <option value={person.id} key={person.id}>{person.name}</option>)}
                          </select>
                        </label>
                      )}
                      <label className="ssb-field" htmlFor={`${choiceKey}-why`}>{text("Why?", "لماذا؟")}</label>
                      <textarea
                        id={`${choiceKey}-why`}
                        rows={2}
                        required
                        value={existing?.reason || ""}
                        onChange={(event) => updateChoice(item.itemType, item.itemId, { reason: event.currentTarget.value })}
                        placeholder={text("Required explanation", "التوضيح مطلوب")}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </section>
  );
}
